import { matchPath } from "react-router-dom";
import {
  Buildings,
  Calculator,
  FileText,
  House,
  type Icon,
  ListChecks,
  Receipt,
  Users,
  Wallet,
} from "@phosphor-icons/react";
import { ROUTES } from "@/app/routes";

export const SHELL_CONTENT_ID = "shell-content";

export type ShellNavigationGroup = "" | "shell.clients" | "shell.workspace" | "shell.tools";

export const SHELL_NAVIGATION_GROUPS: ShellNavigationGroup[] = [
  "",
  "shell.clients",
  "shell.workspace",
  "shell.tools",
];

export const SHELL_NAVIGATION = [
  { label: "nav.dashboard", path: ROUTES.DASHBOARD, icon: House, group: "" },
  { label: "shell.pending", path: ROUTES.TASKS, icon: ListChecks, group: "" },
  {
    label: "nav.beneficiaries",
    path: ROUTES.BENEFICIARIES.LIST,
    icon: Buildings,
    group: "shell.clients",
  },
  {
    label: "nav.investors",
    path: ROUTES.INVESTMENTS.INVESTORS,
    icon: Users,
    group: "shell.clients",
  },
  {
    label: "nav.investments",
    path: ROUTES.INVESTMENTS.MAIN,
    icon: Wallet,
    group: "shell.workspace",
  },
  { label: "shell.quotas", path: ROUTES.QUOTAS, icon: Calculator, group: "shell.workspace" },
  {
    label: "nav.contracts",
    path: ROUTES.INVESTMENTS.CONTRACTS,
    icon: Receipt,
    group: "shell.workspace",
  },
  {
    label: "nav.calculator",
    path: ROUTES.INVESTMENTS.CALCULATOR,
    icon: Calculator,
    group: "shell.tools",
  },
  {
    label: "nav.templates",
    path: ROUTES.ORGANIZATION.TEMPLATES,
    icon: FileText,
    group: "shell.tools",
  },
] as const satisfies readonly {
  label: string;
  path: string;
  icon: Icon;
  group: ShellNavigationGroup;
}[];

export const SHELL_ROUTES = [
  { path: ROUTES.DASHBOARD, label: "nav.dashboard" },
  { path: ROUTES.TASKS, label: "shell.pending" },
  { path: ROUTES.ACTIVITY, label: "shell.activity" },
  { path: ROUTES.QUOTAS, label: "shell.quotas" },
  { path: ROUTES.BENEFICIARIES.LIST, label: "nav.beneficiaries" },
  { path: ROUTES.BENEFICIARIES.DETAIL(":id"), label: "nav.beneficiaries" },
  { path: ROUTES.BENEFICIARIES.QUOTA(":id", ":quotaId"), label: "nav.beneficiaries" },
  { path: ROUTES.BENEFICIARIES.AUDIT(":id", ":quotaId"), label: "nav.beneficiaries" },
  {
    path: ROUTES.BENEFICIARIES.INVESTMENT(":id", ":quotaId", ":investmentId"),
    label: "nav.beneficiaries",
  },
  { path: ROUTES.INVESTMENTS.INVESTORS, label: "nav.investors" },
  { path: ROUTES.INVESTORS.DETAIL(":id"), label: "nav.investors" },
  { path: ROUTES.INVESTORS.INVESTMENT(":id", ":investmentId"), label: "nav.investors" },
  { path: ROUTES.INVESTORS.PROPOSAL(":id", ":proposalId"), label: "nav.investors" },
  { path: ROUTES.INVESTMENTS.MAIN, label: "nav.investments" },
  { path: ROUTES.INVESTMENTS.DETAIL(":uuid"), label: "nav.investments" },
  { path: ROUTES.INVESTMENTS.CONTRACTS, label: "nav.contracts" },
  { path: ROUTES.INVESTMENTS.CONTRACT_DETAIL(":uuid"), label: "nav.contracts" },
  { path: ROUTES.INVESTMENTS.CALCULATOR, label: "nav.calculator" },
  { path: ROUTES.ORGANIZATION.TEMPLATES, label: "nav.templates" },
  { path: ROUTES.ORGANIZATION.TEMPLATE_DETAIL(":id"), label: "nav.templates" },
  { path: ROUTES.PROJECTS.DETAIL(":id"), label: "shell.project" },
] as const;

export function getShellPageLabel(pathname: string) {
  return SHELL_ROUTES.find(({ path }) => matchPath(path, pathname))?.label;
}

export function getShellCollectionPath(pathname: string) {
  const label = getShellPageLabel(pathname);
  return SHELL_ROUTES.find((route) => route.label === label && !route.path.includes(":"))?.path;
}

export function isShellNavigationActive(path: string, pathname: string) {
  return pathname === path || (path !== ROUTES.DASHBOARD && pathname.startsWith(`${path}/`));
}
